import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Switch, message, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { categoriaDespesaService } from '../services/api';
import ShortId from './common/ShortId';

const CategoriasDespesaList = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCategorias();
  }, []);

  const fetchCategorias = async () => {
    setLoading(true);
    try {
      const response = await categoriaDespesaService.list();
      setCategorias(response.data);
    } catch (error) {
      message.error('Erro ao carregar categorias');
      console.error(error);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      nome: record.nome,
      descricao: record.descricao,
      is_ativa: !!record.is_ativa,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Confirmação',
      content: 'Deseja excluir esta categoria?',
      okText: 'Sim',
      cancelText: 'Não',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await categoriaDespesaService.delete(id);
          message.success('Categoria excluída com sucesso');
          fetchCategorias();
        } catch (error) {
          message.error('Erro ao excluir categoria');
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        nome: values.nome,
        descricao: values.descricao || null,
        is_ativa: !!values.is_ativa,
      };

      if (editingId) {
        await categoriaDespesaService.update(editingId, payload);
        message.success('Categoria atualizada com sucesso');
      } else {
        await categoriaDespesaService.create(payload);
        message.success('Categoria criada com sucesso');
      }
      setIsModalVisible(false);
      fetchCategorias();
    } catch (error) {
      message.error('Erro ao salvar categoria');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      render: (id) => <ShortId id={id} />,
    },
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
    },
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',
    },
    {
      title: 'Status',
      dataIndex: 'is_ativa',
      key: 'is_ativa',
      width: 100,
      render: (isAtiva) => (
        <Tag color={isAtiva ? 'green' : 'red'}>
          {isAtiva ? 'Ativo' : 'Inativo'}
        </Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.id)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Nova Categoria
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={categorias}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingId ? 'Editar Categoria' : 'Nova Categoria'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ is_ativa: true }}
        >
          <Form.Item
            name="nome"
            label="Nome"
            rules={[{ required: true, message: 'Nome é obrigatório' }]}
          >
            <Input placeholder="Ex: Material Didático" />
          </Form.Item>

          <Form.Item name="descricao" label="Descrição">
            <Input.TextArea rows={3} placeholder="Descrição da categoria" />
          </Form.Item>

          <Form.Item name="is_ativa" label="Status" valuePropName="checked">
            <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoriasDespesaList;
